import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  API_SPECIFICATION_CONSTANTS,
  DOCUMENTS_API_CONSTANTS,
  NIFTY_APPLICATIONID_KEY,
  PAGING_HEADERS,
} from '@app/app.constants';
import {
  Document,
  DocumentApprovalRequest,
} from '@app/modules/service-data/types/service-data.types';
import { ApiFilters, DocumentSagaState } from '@core/core.types';
import { HttpUtils } from '@core/utils/http-utils';
import { environment } from '@root/environments/environment';
import { Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private httpClient = inject(HttpClient);

  private API_URL = `${environment.apiUrl}/service-data/documents`;

  private routes = {
    getDocument: (documentId: string) =>
      `${environment.apiUrl}/${DOCUMENTS_API_CONSTANTS.documents}/${DOCUMENTS_API_CONSTANTS.actorDocumentProjector}/${documentId}/${API_SPECIFICATION_CONSTANTS.ask}/${DOCUMENTS_API_CONSTANTS.commandGetDocument}`,
    listDocuments: () =>
      `${environment.apiUrl}/${DOCUMENTS_API_CONSTANTS.documents}/${DOCUMENTS_API_CONSTANTS.actorDocumentDirectory}/!/${API_SPECIFICATION_CONSTANTS.ask}/${DOCUMENTS_API_CONSTANTS.queryListDocuments}`,
    getDocumentReadUri: (documentId: string) =>
      `${environment.apiUrl}/${DOCUMENTS_API_CONSTANTS.documents}/${DOCUMENTS_API_CONSTANTS.actorDocument}/${documentId}/${API_SPECIFICATION_CONSTANTS.ask}/GetDocumentReadUri`,
  };

  getDocument(documentId: string) {
    return this.httpClient.get(this.routes.getDocument(documentId));
  }

  listDocuments(
    lastStatus?: string,
    params?: ApiFilters
  ): Observable<Document[]> {
    if (params?.ids?.length === 0) {
      return of([]);
    }

    const httpParams = this.buildHttpParams(lastStatus, params);

    const requestOptions = {
      headers: new HttpHeaders({
        [PAGING_HEADERS.top]: '500',
        [PAGING_HEADERS.orderBy]: 'LastStatusChanged',
        [PAGING_HEADERS.orderDirection]: 'Desc',
      }),
      params: httpParams,
    };
    return this.httpClient.get<Document[]>(
      this.routes.listDocuments(),
      requestOptions
    );
  }

  approveDocument(documentId: string, requestBody: DocumentApprovalRequest) {
    return this.httpClient.put(
      `${this.API_URL}/${documentId}/approve`,
      requestBody ?? {}
    );
  }

  archiveDocument(documentId: string) {
    return this.httpClient.patch(
      `${environment.apiUrl}/documents/${documentId}/archive`,
      {}
    );
  }

  disapproveDocument(documentId: string) {
    return this.httpClient.put(`${this.API_URL}/${documentId}/disapprove`, {});
  }

  deleteDocument(documentId: string) {
    return this.httpClient.post(
      `${environment.apiUrl}/documents${environment.actorsPrefix}/Document/${documentId}/tell/DeleteDocument`,
      {}
    );
  }

  getDocumentReadUri(documentId: string): Observable<string> {
    return this.httpClient
      .get<string>(this.routes.getDocumentReadUri(documentId))
      .pipe(retry(2));
  }

  getDocumentContent(documentUrl: string): Observable<Blob> {
    return this.httpClient.get(documentUrl, { responseType: 'blob' });
  }

  massApproveDocuments(documentIds: string[]) {
    return this.httpClient.post(
      `${environment.apiUrl}/service-data/documents/batch`,
      {
        documentIds,
      }
    );
  }

  checkApprovalState(id: string): Observable<DocumentSagaState> {
    return this.httpClient.get<DocumentSagaState>(
      `${environment.apiUrl}/service-data/documents/batch/${id}`
    );
  }

  confirmMassDocumentApproval(id: string) {
    return this.httpClient.post<any>(
      `${environment.apiUrl}/service-data/documents/batch/${id}/confirm`,
      {}
    );
  }

  buildHttpParams(lastStatus: string, params: ApiFilters) {
    let httpParams = HttpUtils.buildHttpParams(params)
      .set('ApplicationId', NIFTY_APPLICATIONID_KEY)
      .set('IncludeInteractions', 'true');

    if (lastStatus) {
      httpParams = httpParams.set('LastStatus', lastStatus);
    }

    return httpParams;
  }
}
